import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataFetch, authFetch } from '../api';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ZenGridLogo from '../common/ZenGridLogo';
import SettingsModal from './SettingsModal';
import { useUI } from '../contexts/UIContext';

const BLOCK_TYPES = ['CORE', 'BLOCK_1', 'BLOCK_2', 'BLOCK_3', 'BLOCK_4', 'BLOCK_5', 'BLOCK_6', 'BLOCK_7', 'BLOCK_8'];

const assertSuccess = (res, fallbackMessage) => {
  if (!res || res.status === 'error') {
    throw new Error(res?.error || fallbackMessage);
  }
  return res;
};

const Dashboard = () => {
  const [charts, setCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [editingChartId, setEditingChartId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef(null);
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id');
  const { toast, confirm } = useUI();

  const checkAuthError = (res) => {
    if (res?.httpStatus === 401) {
      localStorage.removeItem('user_id');
      navigate('/login');
      return true;
    }
    return false;
  };

  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const res = await dataFetch(`read/mandalacharts?owner_id=${userId}&sort=id&order=desc`);
        if (checkAuthError(res)) return;
        setCharts(res?.data ?? []);
      } catch (err) {
        console.error('Failed to fetch charts:', err);
        toast('チャート一覧の取得に失敗しました', 'error');
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchCharts();
  }, [userId]);

  const createBlockWithCells = async (chartId, type) => {
    const blockRes = await dataFetch(`create/blocks?owner_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify({ mandalachart_id: chartId, block_type: type, user_id: userId })
    });
    if (checkAuthError(blockRes)) throw new Error('セッションが期限切れです');
    assertSuccess(blockRes, `ブロック(${type})の作成に失敗しました`);

    const blockId = blockRes?.data?.id ?? blockRes?.id;
    if (!blockId) throw new Error(`ブロック(${type})のIDが取得できませんでした`);

    const cellPromises = [
      dataFetch(`create/cells?owner_id=${userId}`, {
        method: 'POST',
        body: JSON.stringify({ block_id: blockId, is_center: 1, priority_index: 0, content: '', user_id: userId })
      })
    ];
    for (let i = 1; i <= 8; i++) {
      cellPromises.push(
        dataFetch(`create/cells?owner_id=${userId}`, {
          method: 'POST',
          body: JSON.stringify({ block_id: blockId, is_center: 0, priority_index: i, content: '', user_id: userId })
        })
      );
    }
    const cellResults = await Promise.all(cellPromises);
    cellResults.forEach((res) => { if (checkAuthError(res)) throw new Error('セッションが期限切れです'); });
    cellResults.forEach((res) => assertSuccess(res, `ブロック(${type})のセル作成に失敗しました`));
    return { id: blockId, block_type: type };
  };

  const handleCreateNew = async () => {
    if (!userId) {
      toast('ユーザー情報が見つかりません。再度ログインしてください。', 'error');
      navigate('/login');
      return;
    }
    setCreating(true);
    try {
      const chartRes = await dataFetch(`create/mandalacharts?owner_id=${userId}`, {
        method: 'POST',
        body: JSON.stringify({ title: 'ZenGrid チャート', user_id: userId })
      });
      if (checkAuthError(chartRes)) throw new Error('セッションが期限切れです');
      assertSuccess(chartRes, 'チャート本体の作成に失敗しました');

      const chartId = chartRes?.data?.id ?? chartRes?.id;
      if (!chartId) throw new Error('チャートIDが取得できませんでした');

      await Promise.all(BLOCK_TYPES.map((type) => createBlockWithCells(chartId, type)));
      navigate(`/chart/${chartId}`);
    } catch (err) {
      console.error('Create error:', err);
      toast('作成処理中にエラーが発生しました: ' + err.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleRenameChart = async (chartId, newTitle) => {
    if (!newTitle.trim()) {
      setEditingChartId(null);
      return;
    }
    try {
      const res = await dataFetch(`update/mandalacharts/${chartId}?owner_id=${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ title: newTitle.trim(), user_id: userId })
      });
      if (checkAuthError(res)) return;
      assertSuccess(res, 'チャート名の更新に失敗しました');
      setCharts(prev => prev.map(c => c.id === chartId ? { ...c, title: newTitle.trim() } : c));
      toast('チャート名を更新しました', 'success');
    } catch (err) {
      console.error(err);
      toast('チャート名の更新に失敗しました', 'error');
    }
    setEditingChartId(null);
  };

  const startEditing = (chart) => {
    setEditingChartId(chart.id);
    setEditingTitle(chart.title || '');
    setTimeout(() => { if (editInputRef.current) editInputRef.current.focus(); }, 50);
  };

  const handleDelete = async (e, chartId) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await confirm('本当にこのチャートを削除しますか？\n関連するすべてのデータが削除されます。', {
      title: 'チャートを削除', confirmLabel: '削除する', danger: true,
    });
    if (!ok) return;
    try {
      const blocksRes = await dataFetch(`read/blocks?mandalachart_id=${chartId}&owner_id=${userId}`);
      if (checkAuthError(blocksRes)) return;
      const blocks = blocksRes?.data ?? [];
      if (blocks.length > 0) {
        const blockIds = blocks.map(b => b.id);
        const cellsRes = await dataFetch(`read/cells?block_id[in]=${blockIds.join(',')}&owner_id=${userId}&limit=100`);
        const cells = cellsRes?.data ?? [];
        if (cells.length > 0) {
          await Promise.all(cells.map(c => dataFetch(`delete/cells/${c.id}?owner_id=${userId}`, { method: 'DELETE' })));
        }
        await Promise.all(blocks.map(b => dataFetch(`delete/blocks/${b.id}?owner_id=${userId}`, { method: 'DELETE' })));
      }
      await dataFetch(`delete/mandalacharts/${chartId}?owner_id=${userId}`, { method: 'DELETE' });
      setCharts(prev => prev.filter(c => c.id !== chartId));
      toast('チャートを削除しました', 'success');
    } catch (err) {
      console.error(err);
      toast('削除処理中にエラーが発生しました', 'error');
    }
  };

  const handleLogout = async () => {
    await authFetch('sign-out', { method: 'POST' });
    localStorage.removeItem('user_id');
    navigate('/login');
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden bg-gradient-to-br from-slate-100 via-indigo-50 to-sky-100 dark:from-[#0a0a12] dark:via-[#0f0f1a] dark:to-[#111124] transition-colors duration-300">
      <div className="pointer-events-none fixed -top-32 -left-32 w-96 h-96 bg-primary-400/30 dark:bg-primary-500/20 rounded-full blur-[100px]" />
      <div className="pointer-events-none fixed top-1/3 -right-32 w-96 h-96 bg-sky-300/30 dark:bg-indigo-500/20 rounded-full blur-[100px]" />

      <div className="relative max-w-5xl mx-auto p-6 md:p-10">
        <header className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3 tracking-tight">
            <span className="w-11 h-11 rounded-2xl overflow-hidden shadow-lg shadow-primary-600/20">
              <ZenGridLogo className="w-full h-full" />
            </span>
            ZenGrid
          </h1>
          <div className="flex gap-3">
            <button onClick={() => setIsSettingsOpen(true)} className="px-4 py-2.5 backdrop-blur-xl bg-white/60 dark:bg-white/5 text-gray-800 dark:text-white rounded-full border border-white/70 dark:border-white/10 shadow-sm hover:bg-white/80 dark:hover:bg-white/10 flex items-center gap-2 transition-all text-sm font-semibold">
              <SafeIcon icon={FiIcons.FiSettings} /> 設定
            </button>
            <button onClick={handleLogout} className="px-4 py-2.5 backdrop-blur-xl bg-red-500/10 text-red-600 dark:text-red-400 rounded-full border border-red-500/20 hover:bg-red-500/20 flex items-center gap-2 transition-all text-sm font-semibold">
              <SafeIcon icon={FiIcons.FiLogOut} /> ログアウト
            </button>
          </div>
        </header>

        <div className="mb-10">
          <button onClick={handleCreateNew} disabled={creating} className="w-full py-6 bg-primary-600 hover:bg-primary-500 active:bg-primary-700 text-white rounded-3xl shadow-xl shadow-primary-600/25 hover:shadow-2xl hover:shadow-primary-600/30 flex items-center justify-center gap-3 transition-all disabled:opacity-70 text-lg font-bold border border-white/20">
            {creating ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-2xl" /> : <SafeIcon icon={FiIcons.FiPlusCircle} className="text-2xl" />}
            {creating ? 'ワークスペースを準備中...' : '新しいチャートを作成'}
          </button>
        </div>

        <div>
          <h2 className="text-base font-bold text-gray-800 dark:text-gray-100 mb-5 flex items-center gap-2 tracking-tight">
            <SafeIcon icon={FiIcons.FiClock} className="text-primary-600 dark:text-primary-400" />
            過去のチャート
          </h2>
          {loading ? (
            <div className="flex justify-center p-10"><SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-3xl text-primary-500" /></div>
          ) : charts.length === 0 ? (
            <div className="text-center p-12 backdrop-blur-xl bg-white/50 dark:bg-white/5 rounded-3xl border border-dashed border-gray-400/40 dark:border-white/15 text-gray-600 dark:text-gray-300 font-medium">
              まだチャートがありません。「新しいチャートを作成」から開始してください。
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {charts.map(chart => (
                <div
                  key={chart.id}
                  onClick={() => { if (editingChartId !== chart.id) navigate(`/chart/${chart.id}`); }}
                  className="relative backdrop-blur-xl bg-white/60 dark:bg-white/5 p-6 rounded-3xl border border-white/70 dark:border-white/10 shadow-sm hover:shadow-xl hover:-translate-y-0.5 hover:border-primary-400/60 dark:hover:border-primary-500/50 cursor-pointer transition-all group overflow-hidden"
                >
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity z-20 flex gap-1">
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEditing(chart); }}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-500/10 rounded-full transition-colors relative z-30"
                      title="名前変更"
                    >
                      <SafeIcon icon={FiIcons.FiEdit3} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, chart.id)}
                      className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-full transition-colors relative z-30"
                      title="削除"
                    >
                      <SafeIcon icon={FiIcons.FiTrash2} />
                    </button>
                  </div>

                  {editingChartId === chart.id ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameChart(chart.id, editingTitle);
                        if (e.key === 'Escape') setEditingChartId(null);
                      }}
                      onBlur={() => handleRenameChart(chart.id, editingTitle)}
                      className="w-full font-bold text-gray-900 dark:text-white text-lg bg-white/80 dark:bg-white/10 rounded-xl px-3 py-1.5 border border-primary-400 outline-none focus:ring-2 focus:ring-primary-500 mb-3"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-3 group-hover:text-primary-600 dark:group-hover:text-primary-400 pr-16 line-clamp-2">
                      {chart.title || 'ZenGrid チャート'}
                    </h3>
                  )}

                  <div className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1.5 font-medium">
                    <SafeIcon icon={FiIcons.FiCalendar} />
                    作成日: {chart.created_at ? new Date(chart.created_at).toLocaleDateString() : '最近'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </div>
  );
};

export default Dashboard;